import React from 'react';
import { Settings, Calculator, HelpCircle } from 'lucide-react';
import type { GameSettings as GameSettingsType, Operation, Difficulty } from '../types';

interface GameSettingsProps {
  settings: GameSettingsType;
  onSettingsChange: (settings: GameSettingsType) => void;
}

const DIFFICULTIES: { value: Difficulty; label: string; description: string }[] = [
  { value: 'easy', label: 'Łatwy', description: 'Liczby od 1 do 10' },
  { value: 'medium', label: 'Średni', description: 'Liczby od 1 do 100' },
  { value: 'hard', label: 'Trudny', description: 'Liczby od 1 do 1000' }
];

const OPERATIONS: { value: Operation; label: string; symbol: string }[] = [
  { value: 'add', label: 'Dodawanie', symbol: '+' },
  { value: 'subtract', label: 'Odejmowanie', symbol: '-' },
  { value: 'multiply', label: 'Mnożenie', symbol: '×' },
  { value: 'divide', label: 'Dzielenie', symbol: '÷' }
];

export function GameSettings({ settings, onSettingsChange }: GameSettingsProps) {
  const [showHelp, setShowHelp] = React.useState(false);

  const handleOperationToggle = (operation: Operation) => {
    const newOperations = settings.selectedOperations.includes(operation)
      ? settings.selectedOperations.filter(op => op !== operation)
      : [...settings.selectedOperations, operation];

    if (newOperations.length > 0) {
      onSettingsChange({
        ...settings,
        selectedOperations: newOperations,
        mixedOperations: newOperations.length > 1
      });
    }
  };

  const handleDifficultyChange = (difficulty: Difficulty) => {
    onSettingsChange({
      ...settings,
      difficulty
    });
  };

  const handleMixedOperationsToggle = () => {
    if (!settings.mixedOperations && settings.selectedOperations.length === 1) {
      // If enabling mixed operations, add all operations
      onSettingsChange({
        ...settings,
        mixedOperations: true,
        selectedOperations: ['add', 'subtract', 'multiply', 'divide']
      });
    } else {
      // If disabling mixed operations, keep only the first selected operation
      onSettingsChange({
        ...settings,
        mixedOperations: false,
        selectedOperations: [settings.selectedOperations[0]]
      });
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          <Settings className="w-6 h-6 mr-2" />
          Ustawienia gry
        </h2>
        <button
          onClick={() => setShowHelp(prev => !prev)}
          className="p-2 hover:bg-blue-50 rounded-full transition-colors"
          aria-label="Pokaż instrukcje"
        >
          <HelpCircle className="w-6 h-6 text-blue-500" />
        </button>
      </div>

      {showHelp && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg space-y-3 text-sm">
          <h3 className="font-bold text-blue-700">Jak grać? 🎮</h3>
          
          <div>
            <p className="font-semibold text-blue-700">1. Wybierz poziom trudności:</p>
            <ul className="list-disc pl-5 text-blue-600">
              {DIFFICULTIES.map(diff => (
                <li key={diff.value}>
                  <span className="font-medium">{diff.label}</span>: {diff.description}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold text-blue-700">2. Wybierz działania:</p>
            <ul className="list-disc pl-5 text-blue-600">
              {OPERATIONS.map(op => (
                <li key={op.value}>
                  <span className="font-medium">{op.label}</span> ({op.symbol})
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold text-blue-700">3. Mieszane działania:</p>
            <p className="text-blue-600">
              Włącz tę opcję, aby ćwiczyć różne działania na raz!
            </p>
          </div>

          <div className="!mt-4 p-3 bg-white rounded-md border border-blue-200">
            <p className="font-semibold text-blue-700">Wskazówki:</p>
            <ul className="list-disc pl-5 text-blue-600">
              <li>Zacznij od łatwego poziomu i jednego działania</li>
              <li>Gdy czujesz się pewnie, zwiększ poziom trudności</li>
              <li>Za każde poprawne rozwiązanie zdobywasz punkty!</li>
              <li>Zbieraj naklejki za dobre odpowiedzi</li>
            </ul>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <h3 className="font-semibold mb-2">Poziom trudności:</h3>
          <div className="grid grid-cols-3 gap-2">
            {DIFFICULTIES.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleDifficultyChange(value)}
                className={`p-2 rounded-md transition-colors ${
                  settings.difficulty === value
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-2 flex items-center">
            <Calculator className="w-5 h-5 mr-2" />
            Działania:
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {OPERATIONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleOperationToggle(value)}
                className={`p-2 rounded-md transition-colors ${
                  settings.selectedOperations.includes(value)
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <span className="font-semibold">Mieszane działania:</span>
          <button
            onClick={handleMixedOperationsToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.mixedOperations ? 'bg-blue-500' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.mixedOperations ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}